/**
 * Analytics de producto (PostHog). Privacy-first para un CRM con PII:
 *  - Se activa SOLO si VITE_POSTHOG_KEY está seteada (si no, todo es no-op).
 *  - autocapture OFF y session recording OFF: no capturamos clicks/inputs ni el
 *    DOM (evita filtrar nombres/direcciones/datos de claims).
 *  - Pageviews manuales con el path SANITIZADO (sin IDs ni query string).
 *  - identify solo con id + rol + company slug. Nunca email/nombre.
 *
 * LA LIBRERÍA SE BAJA BAJO DEMANDA (auditoría 2026-09-15): `import posthog from 'posthog-js'`
 * metía ~200 KB en el chunk de arranque aunque no hubiera clave. Ahora se pide con import() solo
 * con clave y cuando el navegador está ocioso; lo que se capture antes de que llegue se encola.
 */
type PostHog = typeof import('posthog-js').default;

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

let ph: PostHog | null = null;
let enabled = false;
const cola: Array<(p: PostHog) => void> = [];

export function initAnalytics(): void {
  if (!KEY) return; // sin key -> deshabilitado (dev y prod hasta configurar)
  enabled = true;
  const cargar = () => {
    void import('posthog-js').then((m) => {
      const posthog = m.default;
      posthog.init(KEY, {
        api_host: HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
      });
      ph = posthog;
      for (const fn of cola.splice(0)) fn(posthog);
    }).catch(() => { enabled = false; cola.length = 0; });
  };
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) window.requestIdleCallback(cargar, { timeout: 4000 });
  else setTimeout(cargar, 1500);
}

/** Ejecuta con la instancia cargada, o lo deja en cola hasta que llegue. */
function conPosthog(fn: (p: PostHog) => void): void {
  if (!enabled) return;
  if (ph) fn(ph); else cola.push(fn);
}

/** Colapsa segmentos que parecen IDs para bajar cardinalidad y no filtrar identificadores. */
export function sanitizePath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (/^\d+$/.test(seg)) return ':id';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(seg)) return ':id'; // uuid
      if (/^[A-Za-z0-9_-]{20,}$/.test(seg)) return ':token'; // tokens largos (ej. intake)
      return seg;
    })
    .join('/');
}

/** Pageview manual (solo el path sanitizado, sin querystring). */
export function trackPageview(pathname: string): void {
  const path = sanitizePath(pathname);
  conPosthog((p) => p.capture('$pageview', { $current_url: path, path }));
}

/** Evento de producto explícito. Pasar solo props no sensibles. */
export function track(event: string, props?: Record<string, unknown>): void {
  conPosthog((p) => p.capture(event, props));
}

/** Identifica al usuario de tenant con datos NO PII (id, rol, company slug). */
export function identifyTenant(user: { id: string; role: string; company_slug: string }): void {
  conPosthog((p) => {
    p.identify(user.id, { role: user.role, company: user.company_slug });
    p.group('company', user.company_slug);
  });
}

/** Limpia la identidad al cerrar sesión. */
export function resetAnalytics(): void {
  conPosthog((p) => p.reset());
}
